/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { JSX } from "react";
import { Delete } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Typography } from "@mui/material";

import Panel from "../../../components/Panel";
import { tl } from "../../../components/translate";
import { TSalary } from "../../../interfaces/SalaryInterface";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import useGetDepartments from "../../../hooks/department";
import { getGenderString } from "../../Campaign/Audience/AudienceHelpers";
import EmployeeInfoPanel from "../../AttendanceDashboard/EmployeInfoPanel";
import SalarySlip from "./Components/SalarySlip";
import { useDeleteSalary } from "../../../hooks/query/useSalaries";
import { rupeesDisplayFull } from "../../../helpers/util";

interface PropTypes {
  onClose: () => void;
  salaryData: TSalary;
  onDeleteSucces: () => void;
  onEditOpen: () => void;
}

export default function SalaryInfoPanel({
  onClose,
  salaryData,
  onDeleteSucces,
  onEditOpen
}: PropTypes): JSX.Element {
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [showEmployeeInfo, setShowEmployeeInfo] = React.useState(false);
  const departments = useGetDepartments();
  const { mutate: deleteSalaryMutation } = useDeleteSalary();

  const handleDelete = () => {
    if (!salaryData?.id) return;
    deleteSalaryMutation(salaryData.id, {
      onSuccess: (response) => {
        if (response.success) {
          onDeleteSucces();
          onClose();
        }
      }
    });
  };

  // Extract salarySummary and earnings/deductions
  const salarySummary = salaryData?.salarySummary;
  const earnings = salaryData?.earnings;
  const bonuses = earnings?.bonuses || {};
  const allowances = earnings?.allowances || {};
  const basicSalary = earnings?.basicSalary || 0;
  const deductions = salaryData?.deductions || {};
  const employee = salaryData?.employee || salaryData?.serviceProvider;
  const retirementContributionsSummary =
    salaryData?.salarySummary?.retirementContributions?.summary;
  const departmentName = employee?.departmentId
    ? departments.find((dept) => String(dept.id) === String(employee.departmentId))?.name || "N/A"
    : "N/A";

  return (
    <>
      <Panel
        onClose={() => onClose()}
        titleJsx={
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6" noWrap>
                {tl("salary.info")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: "8px", gap: 1 }}>
              <IconButton sx={{ cursor: "pointer", p: "5px" }} onClick={() => onEditOpen()}>
                <EditIcon />
              </IconButton>

              <IconButton
                sx={{ cursor: "pointer", p: "5px" }}
                onClick={() => setConfirmDelete(true)}
              >
                <Delete />
              </IconButton>

              <SalarySlip
                salaryData={salaryData}
                departments={departments}
                buttonText="Print Salary Slip"
              />
            </Box>
          </Box>
        }
      >
        {/* Main Info Section */}
        {salaryData && salarySummary && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Info section like CtcInfo */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
                {tl("payroll.basicInformation")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("name")}</Typography>
                <Typography
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    textTransform: "capitalize"
                  }}
                  onClick={() => setShowEmployeeInfo(true)}
                >
                  {employee ? `${employee.firstName} ${employee.lastName}` : "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("payroll.employeeType")}</Typography>
                <Typography>{salaryData.employee ? "Employee" : "Service Provider"}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("department")}</Typography>
                <Typography>{departmentName}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("designation")}</Typography>
                <Typography>{employee?.designation || "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("gender")}</Typography>
                <Typography>{employee?.gender ? getGenderString(employee.gender) : "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("maritalStatus")}</Typography>
                <Typography>{employee?.maritalStatus || "-"}</Typography>
              </Box>
            </Box>

            {/* Basic Salary & Allowances */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
                {tl("payroll.monthlyEarnings")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("payroll.basicSalary")}</Typography>
                <Typography>{rupeesDisplayFull(basicSalary)}</Typography>
              </Box>
              {Object.entries(allowances).map(([key, allowance]) =>
                allowance?.value > 0 ? (
                  <Box key={key} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography width="40%">{tl(`payroll.${key}`)}</Typography>
                    <Typography>{rupeesDisplayFull(allowance.value)}</Typography>
                  </Box>
                ) : null
              )}
            </Box>

            {/* Bonuses */}
            {bonuses && Object.keys(bonuses).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
                  {tl("payroll.bonuses")}
                </Typography>
                {Object.entries(bonuses).map(([bonusType, bonusObj]) =>
                  Object.entries(bonusObj).map(([key, value]) =>
                    Number(value) > 0 ? (
                      <Box
                        key={`${bonusType}-${key}`}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography width="40%">{tl(`payroll.${bonusType}`)}</Typography>
                        <Typography>{rupeesDisplayFull(value)}</Typography>
                      </Box>
                    ) : null
                  )
                )}
              </Box>
            )}

            {/* Deductions */}
            {deductions && Object.keys(deductions).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
                  {tl("payroll.deductions")}
                </Typography>
                {Object.entries(deductions).map(([key, deduction]) =>
                  deduction?.value > 0 ? (
                    <Box key={key} sx={{ display: "flex", alignItems: "center" }}>
                      <Typography width="40%">{tl(`payroll.${key}`)}</Typography>
                      <Typography>{rupeesDisplayFull(deduction.value)}</Typography>
                    </Box>
                  ) : null
                )}
              </Box>
            )}

            {/* Monthly Pay Summary */}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
                {tl("salary.monthlyPaySummary")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("salary.grossMonthlySalary")}</Typography>
                <Typography>
                  {rupeesDisplayFull(salarySummary?.salary?.grossMonthlySalary ?? 0)}
                </Typography>
              </Box>

              {(retirementContributionsSummary?.pfContributions?.employee ?? 0) > 0 && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography width="40%">Employee PF contribution</Typography>
                  <Typography>
                    {rupeesDisplayFull(
                      retirementContributionsSummary?.pfContributions?.employee ?? 0
                    )}
                  </Typography>
                </Box>
              )}

              {(retirementContributionsSummary?.ssfContributions?.employee ?? 0) > 0 && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography width="40%">Employee SSF contribution</Typography>
                  <Typography>
                    {rupeesDisplayFull(
                      retirementContributionsSummary?.ssfContributions?.employee ?? 0
                    )}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography width="40%">{tl("salary.monthlyTaxAmount")}</Typography>
                <Typography>
                  {rupeesDisplayFull(salarySummary?.tax?.netCurrentMonthTax ?? 0)}
                </Typography>
              </Box>
              {salarySummary?.tax?.taxAdjustment !== 0 && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography width="40%">{tl("salary.taxAdjustment")}</Typography>
                  <Typography>
                    {rupeesDisplayFull(salarySummary?.tax?.taxAdjustment ?? 0)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "primary.main",
                  fontWeight: 600
                }}
              >
                <Typography width="40%">{tl("salary.netSalaryMonthly")}</Typography>
                <Typography>
                  {rupeesDisplayFull(salarySummary?.salary?.netMonthlySalary ?? 0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "primary.main",
                  fontWeight: 600
                }}
              >
                <Typography width="40%">{tl("salary.netSalaryMonthly")}</Typography>
                <Typography>
                  {rupeesDisplayFull(
                    salarySummary?.salary?.totalPayable ??
                      salarySummary?.salary?.netMonthlySalary ??
                      0
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Panel>

      {confirmDelete && (
        <OkhatiDialog
          title={tl("create")}
          description="Are you sure you want to delete this salary? This action cannot be undone."
          next={handleDelete}
          cancel={() => setConfirmDelete(false)}
          readMode={false}
        />
      )}

      {showEmployeeInfo && (
        <EmployeeInfoPanel employee={employee || null} onClose={() => setShowEmployeeInfo(false)} />
      )}
    </>
  );
}
